'use client';

import { Check, Database, Mail, MessageCircle, Table2 } from 'lucide-react';
import { AnimatePresence, motion } from 'motion/react';

import { DemoFrame, useDemoLoop } from './_lib/demo';

/**
 * Domain 4 — Delivery. A qualified lead routes in real time into the tools the
 * client already uses. Conveys: clean hand-off, your system owns it next.
 * Honest sub-note: we deliver, we don't promise downstream outcomes.
 */
export interface DeliveryCopy {
  frameLabel: string;
  leadName: string;
  qualified: string;
  destinations: [string, string, string, string];
  delivered: string;
}

const ICONS = [MessageCircle, Database, Table2, Mail] as const;

export function DeliveryAnimation({ copy }: { copy: DeliveryCopy }) {
  // steps: 0 lead ready, 1 routing, 2 destinations active, 3 delivered note
  const { ref, step, fading } = useDemoLoop(4, {
    durations: [1300, 1500, 1400],
    holdMs: 2400,
    offsetMs: 600,
  });

  return (
    <DemoFrame ref={ref} label={copy.frameLabel} fading={fading}>
      <div className="flex h-full flex-col">
        {/* Lead card */}
        <div className="flex items-center justify-between rounded-lg border border-[var(--sel-line)] bg-[var(--sel-bg)] px-3 py-2.5">
          <span className="flex items-center gap-2">
            <span className="flex h-6 w-6 items-center justify-center rounded-full bg-[var(--sel-accent)] text-[0.625rem] font-bold text-white">
              {copy.leadName.slice(0, 2).toUpperCase()}
            </span>
            <span className="text-sm font-medium text-[var(--sel-ink)]">
              {copy.leadName}
            </span>
          </span>
          <span className="inline-flex items-center gap-1 rounded-full bg-[var(--sel-highlight-bright)] px-2 py-0.5 text-[0.625rem] font-semibold text-[var(--sel-highlight-ink)]">
            <Check className="h-3 w-3" strokeWidth={3} />
            {copy.qualified}
          </span>
        </div>

        {/* Routing track with a travelling token. The token rides a
            full-width wrapper so translateX percentages resolve against the
            track width (transform-only, no layout-property animation). */}
        <div className="relative my-3 h-7">
          <div className="absolute top-1/2 right-0 left-0 h-px -translate-y-1/2 bg-[var(--sel-line)]" />
          <motion.div
            className="absolute inset-0"
            animate={{ x: step >= 2 ? '100%' : step >= 1 ? '50%' : '0%' }}
            transition={{ duration: 0.4, ease: [0.22, 0.68, 0.35, 1] }}
          >
            <span className="absolute top-1/2 left-0 block h-2.5 w-2.5 -translate-y-1/2 rounded-full bg-[var(--sel-accent)]" />
          </motion.div>
        </div>

        {/* Destinations */}
        <div className="grid grid-cols-2 gap-2">
          {copy.destinations.map((label, i) => {
            const Icon = ICONS[i] ?? Mail;
            const active = step >= 2;
            return (
              <div
                key={label}
                className={`flex items-center gap-2 rounded-lg border px-2.5 py-2 transition-colors duration-300 ${
                  active
                    ? 'border-[var(--sel-accent)] bg-[var(--sel-accent-soft)]/40'
                    : 'border-[var(--sel-line)] bg-[var(--sel-bg)]'
                }`}
              >
                <Icon
                  className={`h-4 w-4 shrink-0 ${active ? 'text-[var(--sel-accent)]' : 'text-[var(--sel-faint)]'}`}
                />
                <span className="truncate text-xs font-medium text-[var(--sel-ink)]">
                  {label}
                </span>
                <AnimatePresence>
                  {active && (
                    <motion.span
                      initial={{ opacity: 0, scale: 0.5 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: i * 0.06 }}
                      className="ml-auto"
                    >
                      <Check
                        className="h-3.5 w-3.5 text-[var(--sel-accent)]"
                        strokeWidth={3}
                      />
                    </motion.span>
                  )}
                </AnimatePresence>
              </div>
            );
          })}
        </div>

        {/* Honest delivered note */}
        <AnimatePresence>
          {step >= 3 && (
            <motion.p
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              className="mt-auto pt-3 text-center text-xs text-[var(--sel-muted)]"
            >
              {copy.delivered}
            </motion.p>
          )}
        </AnimatePresence>
      </div>
    </DemoFrame>
  );
}
